import { Dictionary } from 'lodash';
import { stringify } from 'query-string';
import { Currency } from 'api/account';
import { CostRate } from 'api/cost-rates';
import { HttpClient } from 'api/http-client';
import { Company, DateType, ProjectId, Timestamp, User } from 'api/models';
import { PartnerCompany } from 'api/partner/types';
import { UserPermissionForProject } from 'api/permissions';
import { Export } from 'api/reports/csat';
import { Space } from 'api/space/space';
import { Sprint } from 'api/sprint-planning/types';
import {
  Field,
  FieldAccessMeta,
  FieldAccessMetaInBase3,
  FieldOption,
  FieldOptionBase,
  FieldValue,
  FieldValueType,
  LinkedResource,
  StatusFieldOption,
  Task,
} from 'api/tasks';
import { ProjectVisibility } from 'api/visibility';
import { Color } from 'components/colors';
import { Filter } from 'components/molecules/filters/types';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { GroupCriteria } from 'components/organisms/tasks/grouped-filter-popover';
import { MembersResponse } from 'data/members/api';
import { InvitedMembers } from 'data/members/slice';
import { CreateAndInviteProjectMembers } from 'data/projects/actions';
import { ProjectTemplateApi } from 'data/templates/projects';
import { PlaceholderMappingResponse } from 'queries/placeholder';
import { fieldAccessMetaToBase3 } from 'utils/field-access';
import {
  ApiFilter,
  ApiGroupFilter,
} from 'utils/simple-search-filter/simple-search-filter';
import { updateProjectsPartners } from 'utils/strategic-partner-utils';
import {
  AddProjectUserLiteProps,
  AddTeamToProjectPayload,
  AddTeamToProjectResponse,
  BulkShiftDatesRequest,
  ProjectBaselineCreateRequest,
  ProjectBaselineGetResponse,
  ProjectBaselineUpdateRequest,
  ProjectBaselineWriteResponse,
  ProjectProgressPlots,
  ProjectUserLiteMembership,
  RemoveProjectUserLiteProps,
  RemoveTeamFromProjectPayload,
  RemoveTeamFromProjectResponse,
  ProjectFinancialsApiData,
  ProjectFinancialsApiQueryParams,
  ProjectFinancialsApiDataWithQuery,
  ProjectFinancialsDetails,
  ProjectFinancialsContractType,
  ShiftTasksPayload,
  DependenciesMap,
  ProjectFinancalsSubscriptionCurrentPeriodData,
} from './types';

export type AsyncProjectCreateKey = ['async-project-create', number, string];

interface PaginatedProjectResponse {
  data: Project[];
  count: number;
}

interface PaginatedProjectParams {
  startRow: number;
  endRow: number;
  filter: GroupCriteria[];
  filterType: QueryCombinerType;
  sort: string;
}

export interface AggregateProgress {
  atRiskCount?: number;
  overDueCount?: number;
  pendingApprovalCount?: number;
  taskCount?: number;
  csatRating?: number;
}

export interface ProjectFieldValue<T> extends Field {
  fieldId: number;
  fieldValue: T;
  metaFieldValue?: FieldOption<T>;
}

// Used for status in the Projects-list page
export interface ProgressStatus
  extends Pick<StatusFieldOption, 'color' | 'value' | 'label' | 'group'> {
  fieldId?: number;
  count?: number;
  key: string;
  type?: string;
}

export interface ProjectPhase {
  fields: FieldValue<FieldValueType>[];
  projectPhaseId: number;
  projectPhaseName: string;
  tasks: Task[];
  dueDate: DateType;
  startDate: DateType;
  private: boolean;
  progressStatus: ProgressStatus[];
  project: Project;
  aggregateProgressValues?: AggregateProgress;
  totalTaskCount?: number;
  entityOrder?: string[];
  dependentProjectPhases?: ProjectPhase[];
  phaseDescription?: string;
}

export interface ProjectWithStatus {
  projectId: number;
  projectName: string;
  statusValue: number;
}

export interface ProjectWithPartners {
  projectId: number;
  partners: PartnerCompany[];
}

export interface ProjectWithFinancialContractType {
  projectId: number;
  projectName: string;
  financialContractType?: ProjectFinancialsContractType;
}

export interface RecordDataProjectPhase extends ProjectPhase {
  projectId: number;
  id: number;
}

export enum VisibilityType {
  ALL = 'EVERYONE',
  INVITED = 'MEMBERS',
}

export enum ProjectStatus {
  InProgress = 2,
  Completed,
  Blocked,
  Proposed,
  InPlanning,
  ToBeStaffed,
  Cancelled,
  OnHold,
}

export enum RestrictedType {
  GROUPS = 'GROUP',
  TEAMMEMBERS = 'MEMBERS',
}

export interface MembersMeta {
  existingEmailDomain: 'string';
  projectMembersMeta: {
    taskAssignedCount: number;
    user: {
      userId: number;
      identityType: string;
    };
  }[];
}

export type ProjectSource = {
  sourceType: string;
  sourceId: number;
  metaData: {
    templateName: string;
  };
  templateName: string;
};

export enum InferredProgressLabels {
  RunningLate = 'Running Late',
  OnTrack = 'On Track',
  AHeadOfTime = 'Ahead of time',
  None = 'None',
}

export enum InferredProgressValue {
  RunningLate = 'RUNNING_LATE',
  OnTrack = 'ON_TRACK',
  AHeadOfTime = 'AHEAD_OF_TIME',
  None = 'NONE',
}

export interface InferredProgress {
  color: Color;
  label: InferredProgressLabels;
  value: InferredProgressValue;
}

export const InferredProgressOptions: FieldOptionBase<InferredProgressValue>[] =
  [
    {
      label: InferredProgressLabels.RunningLate,
      value: InferredProgressValue.RunningLate,
      color: 'red',
      active: true,
    },
    {
      label: InferredProgressLabels.OnTrack,
      value: InferredProgressValue.OnTrack,
      color: 'green',
      active: true,
    },
    {
      label: InferredProgressLabels.AHeadOfTime,
      value: InferredProgressValue.AHeadOfTime,
      color: 'blue',
      active: true,
    },
    {
      label: InferredProgressLabels.None,
      value: InferredProgressValue.None,
      color: 'gray',
      active: true,
    },
  ];
export interface Project {
  annualizedRecurringRevenue: number;
  dueDate: DateType;
  projectId: number;
  projectName: string;
  projectPhases: ProjectPhase[];
  projectOwner: User;
  customerChampion: User;
  currentPhases?: ProjectPhase[];
  customerPortalId?: number;
  inferredProgress?: InferredProgress;
  inferredProgressDays?: number;
  startDate: DateType;
  allocationStartDate?: DateType;
  allocationDueDate?: DateType;
  status: string; // TODO:: This is a part of fieds now, can we remove this @abhi, @sha
  customer: Company;
  spaceList: Space[];
  revenue?: number;
  aggregateProgressValues?: AggregateProgress;
  fields: ProjectFieldValue<FieldValueType>[];
  visibility: VisibilityType;
  visibilitySettings?: ProjectVisibility;
  source?: ProjectSource;
  progressStatus: ProgressStatus[];
  pastProjectStatus: PastProjectStatus[];
  projectFee: number;
  doesSprintsExist: boolean;
  metrics: {
    progressPercent: number;
    currentPhases: ProjectPhase[];
    totalAllocatedHours?: number;
    actualCost?: number;
    actualProfit?: number;
    actualProfitMargin?: number;
    actualRevenue?: number;
    estimatedCost?: number;
    estimatedProfit?: number;
    estimatedProfitMargin?: number;
    estimatedRevenue?: number;
    estimateAtCompletion?: number;
    estimateToComplete?: number;
    trackedHours?: number;
    billableHours?: number;
  };
  teamMembers?: User[];
  /** Only available in list */
  totalMinutes?: number;
  linkedResources?: LinkedResource[];
  /** Timesheet fields */
  budgetedHours?: number;
  budgetedMinutes?: number;
  totalAllocatedHours?: number;
  /* TODO::FIX_ME use it only in project list */
  totalTaskCount?: number;
  createdAt: number;
  autoAllocation?: boolean;
  sourceTemplates?: ProjectSource[];
  template?: number[];
  templates?: number[];
  metaData?: Record<number, unknown>;
  partners: Company[];
  customerMembersAccepted?: number;
  customerMembersInvited?: number;
  currency?: Currency;
  plannedDuration?: number;
  projectAge?: number;
  percentageBudgetedHoursConsumed?: number;
  percentageBudgetConsumed?: number;
  projectFinancialsDetailModel?: ProjectFinancialsDetails;
  financialContractType?: ProjectFinancialsContractType;
  fieldsAccessMeta: FieldAccessMeta;
  fieldsAccessMetaInBase3: FieldAccessMetaInBase3;
  userGroupIds?: number[];
  asyncProjectCreation?: boolean;
  currentPeriodData?: ProjectFinancalsSubscriptionCurrentPeriodData;
  permissionSet: {
    permissionSetId: number;
    permissionSetType: number;
    permissionSetName: string;
  };
}

export type ProjectForRm = Omit<
  Project,
  'field' | 'fieldsAccessMeta' | 'fieldsAccessMetaInBase3'
>;

export interface ArchivedProject extends Project {
  archivedOn: Timestamp;
  archivedBy: User;
}

export interface CurrentPhasesMap {
  visiblePhase: string;
  visiblePhaseProgressStatus: ProgressStatus[];
  otherPhases: string[];
}

export interface ProjectMeta {
  name: string;
  logo: string;
  id: number;
}

export interface ProjectHours {
  budgetMinutes?: number;
  total_minutes: number;
  project_name: string;
  billableMinutes?: number;
  nonBillableHoursFlagEnabled?: boolean;
}
export interface ProjectListItem
  extends Omit<Project, 'status' | 'currentPhases' | 'teamMembers'> {
  lastMilestoneCSAT?: number;
  key: string;
  projectMeta: ProjectMeta;
  projectHours: ProjectHours;
  createdAt: number;
  currentPhases?: CurrentPhasesMap;
  progressPercent: number;
  totalTaskCount: number;
  csatRating: number;
  // mapping arrays to hash
  fieldsMap?: {
    // TODO: remove after converting the main object fields
    [key: string]: ProjectFieldValue<FieldValueType>;
  };
  projectPhasesMap?: ProjectPhaseMap[];
  status: StatusFieldOption;
  aggregatePhaseProgressStatus: ProgressStatus[];
  teamMembers?: Pick<User, 'userId'>[];
}

export interface ProjectUpdatePayload extends Omit<Project, 'fields'> {
  fields?: FieldValue<FieldValueType>[];
}

export enum ProjectSourceType {
  Template = 'TEMPLATE',
}

export type ProjectCreateLinkedResource = Pick<
  LinkedResource,
  | 'integrationId'
  | 'externalResourceType'
  | 'externalResourceId'
  | 'externalIntegrationType'
  | 'enabled'
  | 'context'
>;

interface Source {
  sourceId: number;
  sourceType: ProjectSourceType;
}

export interface PlaceholderAssignmentMap {
  teamId: number;
  userId?: number;
  companyId?: number;
  roleId?: number;
}
export interface CreateProjectPayload {
  customer: Pick<Company, 'companyId'>;
  projectName: string;
  dueDate?: DateType;
  startDate?: DateType;
  source?: Source;
  projectOwner: Pick<User, 'userId'>;
  customerChampion?: Pick<User, 'userId'> | null;
  teamMembers?: Pick<User, 'userId'>[];
  visibility: VisibilityType;
  fields?: Array<FieldValue<FieldValueType>>;
  status?: FieldValue<number>;
  annualizedRecurringRevenue?: number;
  projectFee?: number;
  linkedResources?: ProjectCreateLinkedResource[];
  sources?: ProjectBulkMergePayload[];
  budgetedHours?: number;
  budgetedMinutes?: number;
  teamToUserMapRequests?: PlaceholderAssignmentMap[];
  partners: Pick<Company, 'companyId'>[];
  customerPortalId?: number;
  projectFinancialsDetailModel?: Pick<
    ProjectFinancialsDetails,
    | 'financialContractType'
    | 'rateCardId'
    | 'projectBudget'
    | 'projectHours'
    | 'projectMinutes'
    | 'projectFinancialsMetaData'
  >;
  currency?: Currency;
  userGroupIds?: number[];
}

export type UpdateProjectPayload = Partial<CreateProjectPayload> &
  Pick<Project, 'budgetedHours' | 'budgetedMinutes' | 'customerPortalId'> & {
    shiftAllocations?: boolean;
    autoAllocation?: boolean;
  };

export interface PastProjectStatus {
  projectStatusId: number;
  projectStatusName: string;
  updatedAt: number;
  updatedBy: User;
}

export interface ProjectPhaseMap {
  id: number;
  name?: string;
  inProgress: number;
  completed: number;
  total: number;
  startDate: DateType;
  status: StatusFieldOption;
  private?: boolean;
}

export interface ProjectMembers extends Partial<Project>, MembersResponse {
  accountId?: number | null;
}

export interface CurrentPhase extends ProjectPhase {
  approvalPending: number;
  atRiskCount: number;
  overDueTaskCount: number;
  status: string;
  totalTaskCount: number;
}

export interface ProjectMergePayload {
  source: Pick<ProjectSource, 'sourceType' | 'sourceId'>;
}

export interface ProjectBulkMergePayload {
  source: Source;
  startDate: DateType;
  prefix?: string;
  includeSpaces?: boolean;
}

export interface ProjectToTemplatePayload {
  templateName: string;
  descriptionFlag: boolean;
  attachmentFlag: boolean;
  assignmentsFlag: boolean;
  fieldsFlag: boolean;
  dependenciesFlag: boolean;
}

interface PaginatedParams {
  startRow: number;
  endRow: number;
  filter: Filter;
  sort?: string;
  groupBy?: string;
  search?: string;
  pageSource?: string;
}

export type ProjectBulkMergeResult = Pick<
  Project,
  'projectPhases' | 'spaceList' | 'startDate'
> & { source: Source };

interface GptSearchPayload {
  input: string;
}

export class ProjectAPIClient extends HttpClient {
  public async getProjectList(): Promise<Project[]> {
    return this.get<Project[]>('/projects?lite=true');
  }

  public async createProjectToTemplate(
    projectId: number,
    payload: ProjectToTemplatePayload,
  ): Promise<ProjectTemplateApi> {
    return this.post(`/projects/${projectId}/create-template`, payload);
  }

  public async getProjectsLight(): Promise<Project[]> {
    const data = await this.get<Project[]>('/projects/light');

    const projectData = data.map((project) =>
      project.fieldsAccessMeta != null
        ? {
            ...project,
            fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
              project.fieldsAccessMeta,
            ),
          }
        : project,
    );

    return updateProjectsPartners(projectData) as Project[];
  }

  public async getProjectsLightForRM(): Promise<ProjectForRm[]> {
    const projectData = await this.get<ProjectForRm[]>('/projects/lightRM');

    return updateProjectsPartners(projectData);
  }

  public async getProjectsMeta(): Promise<Project[]> {
    return this.get<Project[]>('/projects/meta');
  }

  public async getArchivedProjectList(): Promise<Project[]> {
    return this.get<Project[]>('/projects/get-archived');
  }

  public async getProjectDetails(
    projectId: number,
    params = {},
  ): Promise<Project> {
    return this.get<Project>(`/projects/${projectId}`, { params }).then(
      (project) =>
        project.fieldsAccessMeta != null
          ? {
              ...project,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                project.fieldsAccessMeta,
              ),
            }
          : project,
    );
  }

  public async getSprints(projectId: number, params = {}): Promise<Sprint[]> {
    return this.get<Sprint[]>(`/projects/${projectId}/sprints`, {
      params,
    });
  }

  public getPaginatedProject = async ({
    startRow,
    endRow,
    filter,
    sort,
    groupBy,
    search,
    pageSource,
  }: PaginatedParams): Promise<PaginatedProjectResponse> => {
    let url = '/projects/lightV1?';
    const filterAsString = ApiFilter.toQueryStringFormat(filter.criteria);

    if (groupBy) {
      url += `&groupBy=${groupBy}&`;
    }
    if (filter.criteria.length) {
      url += `&${filterAsString}&match=${filter.type}&`;
    }

    if (search) {
      url += `searchValue=${search}&`;
    }

    if (sort) {
      url += `sort=${sort}&`;
    }
    if (pageSource) {
      url += `pageSource=${pageSource}&`;
    }
    url += `offset=${startRow}&limit=${endRow - startRow}`;

    return this.get<PaginatedProjectResponse>(url).then((response) => ({
      ...response,
      data: response.data.map((project) =>
        project.fieldsAccessMeta != null
          ? {
              ...project,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                project.fieldsAccessMeta,
              ),
            }
          : project,
      ),
    }));
  };

  public getProjectsByCustomer = async (
    companyId: number,
  ): Promise<Project[]> => {
    return this.get<PaginatedProjectResponse>(
      `/projects/lightV1?project*customer.value=${companyId}`,
    ).then((response) =>
      response.data.map((project) =>
        project.fieldsAccessMeta != null
          ? {
              ...project,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                project.fieldsAccessMeta,
              ),
            }
          : project,
      ),
    );
  };

  public getProjectGroupMeta = async ({
    filter,
    groupName,
    search,
  }: {
    groupName: string;
    filter: Filter;
    search?: string;
  }): Promise<Record<string, number>[]> => {
    let url = `projects/lightV1/group?groupBy=${groupName}`;
    const filterAsString = ApiFilter.toQueryStringFormat(filter.criteria);

    if (search) {
      url += `&searchValue=${search}`;
    }

    if (filter.criteria.length) {
      url += `&${filterAsString}&match=${filter.type}&`;
    }

    return this.get(url);
  };

  public getMembers = async (projectId: number): Promise<ProjectMembers> => {
    if (projectId == null || Number.isNaN(projectId)) {
      return Promise.resolve<ProjectMembers>({
        members: [],
      });
    }

    return this.get<ProjectMembers>(`/projects/${projectId}/members`);
  };

  public async updateProjectPhase(
    projectId: number,
    phaseId: number,
    payload: Partial<ProjectPhase>,
    isSync?: boolean,
  ): Promise<ProjectPhase> {
    return this.put(
      `/projects/${projectId}/phases/${phaseId}${
        isSync ? `?isSync=${true}` : ''
      }`,
      payload,
    );
  }

  public async createProjectPhase(
    projectId: number,
    projectPhase: Partial<ProjectPhase>,
  ): Promise<ProjectPhase> {
    return this.post(`/projects/${projectId}/phases`, projectPhase);
  }

  public async deleteProjectPhase(
    projectId: number,
    projectPhaseId: number,
  ): Promise<void> {
    return this.delete(`/projects/${projectId}/phases/${projectPhaseId}`);
  }

  public async createProject(payload): Promise<Project> {
    return this.post<Project, any>(
      '/projects?hasfieldsAccessMeta=false',
      payload,
    ).then((project) =>
      project.fieldsAccessMeta != null
        ? {
            ...project,
            fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
              project.fieldsAccessMeta,
            ),
          }
        : project,
    );
  }

  public async createAndInviteProjectMembers(
    projectId: number,
    payload: CreateAndInviteProjectMembers[],
  ): Promise<Project> {
    return this.post(`/projects/${projectId}/invite-members`, payload);
  }

  public async getProjectMembers(projectId: number): Promise<ProjectMembers> {
    return this.get(`/projects/${projectId}/get-members`);
  }

  public async updateProject(
    projectId: number,
    payload: UpdateProjectPayload,
  ): Promise<Project> {
    return this.put<Project, UpdateProjectPayload>(
      `/projects/${projectId}`,
      payload,
    ).then((project) =>
      project.fieldsAccessMeta != null
        ? {
            ...project,
            fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
              project.fieldsAccessMeta,
            ),
          }
        : project,
    );
  }

  public async exportProjectAuditLogs(
    projectId: number,
  ): Promise<{ downloadUrl: string }> {
    return this.get(`/projects/${projectId}/project-audit-log/export`);
  }

  public async deleteProject(projectId: number): Promise<void> {
    return this.delete(`/projects/${projectId}`);
  }

  public async archiveProject(projectId: number): Promise<void> {
    return this.post(`/projects/${projectId}/archive`);
  }

  public async unarchiveProject(projectId: number): Promise<void> {
    return this.post(`/projects/${projectId}/unarchive`);
  }

  public async addMembers(
    projectId: number,
    payload: InvitedMembers[],
  ): Promise<InvitedMembers[]> {
    return this.post(
      `/projects/${projectId}/add-members`,
      payload.map((p) => ({
        ...p,
        permissionSet: undefined,
      })),
    );
  }

  public async updateMembers(
    projectId: number,
    payload: {
      user: {
        userId: number;
      };
      permissionSet: {
        permissionSetType: number;
        permissionSetId?: number;
      };
    }[],
  ): Promise<any> {
    return this.put(`/projects/${projectId}/update-members`, payload);
  }

  public async removeMembers(
    projectId: number,
    payload: {
      userId: number;
    }[],
  ): Promise<any> {
    return this.delete(`/projects/${projectId}/remove-members`, {
      data: payload,
    });
  }

  public async getProjectMembersMeta(projectId: number): Promise<MembersMeta> {
    return this.get(`/projects/${projectId}/get-meta`);
  }

  public async getProgress(projectId: number): Promise<ProjectProgressPlots> {
    return this.get(`/projects/${projectId}/progress`);
  }

  public async merge(projectId: number, payload): Promise<any> {
    return this.put(`/projects/${projectId}/merge`, payload);
  }

  public async getPreviewAPIKey(projectId: number): Promise<any> {
    return this.get(`/projects/${projectId}/customer-access`);
  }

  public exportAsCSV = (
    filters: FieldFilter[],
    match: QueryCombinerType,
    columnOrder: string[],
  ): Promise<Export> => {
    // const filterString = ApiFilter.toQueryStringFormat(filters);
    // const filterObject = ApiFilter.convertQueryStringToObject(filterString);

    // const request: ExportRequest = {
    //   match: filters.length ? match : QueryCombinerType.All,
    //   ...filterObject,
    // };
    // const data: ExportPayload = {
    //   statusType: ExportStatusType.ProjectExport,
    //   statusMeta: { request },
    // };
    return this.post<Export, { columnOrder: string[] }>(
      `reports/project-list/export?${ApiFilter.toQueryStringFormat(
        filters,
      )}&${stringify(
        {
          match: filters.length ? match : null,
        },
        { skipNull: true },
      )}`,
      { columnOrder },
    ).then((response) => response);
  };

  public async bulkMergePreview(
    projectId: number,
    payload: ProjectBulkMergePayload[],
  ): Promise<ProjectBulkMergeResult[]> {
    return this.post(`/projects/${projectId}/bulk-merge-preview`, payload);
  }

  public async bulkMerge(
    projectId: number,
    payload: ProjectBulkMergePayload[],
  ): Promise<Project> {
    return this.put(`/projects/${projectId}/bulk-merge`, payload);
  }

  public async addTeams(
    projectId: number,
    payload: AddTeamToProjectPayload[],
  ): Promise<AddTeamToProjectResponse[]> {
    return this.post(`/projects/${projectId}/add-teams`, payload);
  }

  public async removeTeams(
    projectId: number,
    payload: RemoveTeamFromProjectPayload[],
  ): Promise<RemoveTeamFromProjectResponse[]> {
    return this.delete(`/projects/${projectId}/remove-teams`, {
      data: payload,
    });
  }

  public async getAllProjectUsersLite(): Promise<ProjectUserLiteMembership[]> {
    return this.get('/projects/get-user-teams');
  }

  public async getProjectUsersLiteForProject(
    projectId: number,
  ): Promise<ProjectUserLiteMembership> {
    return this.get(`/projects/${projectId}/get-user-teams`);
  }

  public async addProjectUsersLite(
    projectId: number,
    payload: AddProjectUserLiteProps,
  ): Promise<AddProjectUserLiteProps> {
    return this.post(`/projects/${projectId}/add-user-teams`, payload);
  }

  public async removeProjectUsersLite(
    projectId: number,
    payload: RemoveProjectUserLiteProps,
  ): Promise<RemoveProjectUserLiteProps> {
    return this.delete(`/projects/${projectId}/remove-user-teams`, {
      data: payload,
    });
  }

  public getProjectBaselines = (
    projectId: number,
  ): Promise<ProjectBaselineGetResponse> =>
    this.get<ProjectBaselineGetResponse>(`/projects/${projectId}/baseline`);

  public createProjectBaseline = (
    projectId: number,
    payload: ProjectBaselineCreateRequest,
  ): Promise<ProjectBaselineWriteResponse> =>
    this.post(`/projects/${projectId}/baseline`, payload);

  public updateProjectBaseline = (
    projectId: number,
    baselineId: number,
    payload: Pick<ProjectBaselineUpdateRequest, 'baselineName'>,
  ): Promise<ProjectBaselineWriteResponse> =>
    this.put(`/projects/${projectId}/baseline/${baselineId}`, payload);

  public deleteProjectBaseline = (
    projectId: number,
    baselineId: number,
  ): Promise<ProjectBaselineWriteResponse> =>
    this.delete(`/projects/${projectId}/baseline/${baselineId}`);

  public async getPlaceholderResolution([
    ,
    projectId,
  ]: ProjectId): Promise<PlaceholderMappingResponse> {
    return this.get(`/projects/${projectId}/get-team-sources`);
  }

  public async getAllProjectsPlaceholderResolution(): Promise<PlaceholderMappingResponse> {
    return this.get('/projects/get-team-sources');
  }

  public addPartners = (
    projectId: number,
    partnerCompanyIds: number[],
  ): Promise<any> =>
    this.post(
      `/projects/${projectId}/add-partner`,
      partnerCompanyIds.map((id) => ({ companyId: id })),
    );

  public removePartners = (
    projectId: number,
    partnerCompanyIds: number[],
  ): Promise<any> =>
    this.post(
      `/projects/${projectId}/remove-partner`,
      partnerCompanyIds.map((id) => ({ companyId: id })),
    );

  public bulkShiftDates = (
    projectId: number,
    payload: BulkShiftDatesRequest,
  ): Promise<void> =>
    this.post(`/projects/${projectId}/bulk-shift-dates`, payload);

  public paginateAndFilter = ({
    startRow,
    endRow,
    filter,
    sort,
    filterType,
  }: PaginatedProjectParams): Promise<PaginatedProjectResponse> => {
    let url = '/projects/lightV1?';
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);

    if (filter.length) {
      url += `${filterAsString}&match=${filterType}&`;
    }
    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.get<PaginatedProjectResponse>(url).then((response) => ({
      ...response,
      data: response.data.map((project) =>
        project.fieldsAccessMeta != null
          ? {
              ...project,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                project.fieldsAccessMeta,
              ),
            }
          : project,
      ),
    }));
  };

  public getAllProjectCurrentPhases = (): Promise<ProjectPhase[]> =>
    this.get('/projects/current-phases');

  public gpt({ input }: GptSearchPayload): Promise<Project[]> {
    return this.post<Project[], GptSearchPayload>('/search', { input });
  }

  public getVariablesData = (projectId: number): Promise<any> =>
    this.get(`/variables/${projectId}`);

  public getProjectFinancialsData = (
    url: string,
  ): Promise<ProjectFinancialsApiData> => this.get(url);

  // Keeping two separate APIs below in order to handle Types
  public getProjectFinancials = async ({
    projectId,
    includeSubscriptionMeta,
    subscriptionPeriodId,
    queryParams,
  }: {
    projectId: number;
    includeSubscriptionMeta?: boolean;
    subscriptionPeriodId?: string;
    queryParams?: ProjectFinancialsApiQueryParams;
  }): Promise<ProjectFinancialsApiData> =>
    this.getProjectFinancialsData(
      `/projects/${projectId}/financials${
        includeSubscriptionMeta
          ? '?includeSubscriptionMeta=true'
          : subscriptionPeriodId != null
          ? `?periodId=${subscriptionPeriodId}`
          : ''
      }${
        queryParams != null
          ? `&${stringify(
              {
                groupBy: queryParams.groupBy,
                zoomLevel: queryParams.zoomLevel,
                tabType: queryParams.tabType,
              },
              {
                skipNull: true,
              },
            )}`
          : ''
      }`,
    );

  public getProjectFinancialsWithQuery = async (
    projectId: number,
    queryParams: ProjectFinancialsApiQueryParams,
    subscriptionPeriodId?: string,
  ): Promise<ProjectFinancialsApiDataWithQuery> =>
    this.get(
      `/projects/${projectId}/financials?${stringify(
        {
          groupBy: queryParams.groupBy,
          zoomLevel: queryParams.zoomLevel,
          tabType: queryParams.tabType,
        },
        {
          skipNull: true,
        },
      )}${
        subscriptionPeriodId != null ? `&periodId=${subscriptionPeriodId}` : ''
      }`,
    );

  public getProjectPermissionsForCurrentUser = (
    projectId: number,
  ): Promise<UserPermissionForProject> =>
    this.get(`/projects/${projectId}/permissions`);

  public getProjectBudgetSettings = async (
    projectId: number,
  ): Promise<ProjectFinancialsDetails> =>
    this.get(`projects/${projectId}/financials/settings`);

  public getProjectUserTeamsCostRates = async (
    projectId: number,
  ): Promise<CostRate[]> => this.get(`projects/${projectId}/cost-rates`);

  public updateProjectBudgetSettings = async (
    projectId: number,
    payload: ProjectFinancialsDetails,
  ): Promise<ProjectFinancialsDetails> =>
    this.put(`projects/${projectId}/financials/settings`, payload);

  public shiftTasks = async (
    projectId: number,
    payload: ShiftTasksPayload,
  ): Promise<ShiftTasksPayload> =>
    this.put(`projects/${projectId}/rearrange-phases-and-tasks`, {
      ...payload,
      useEntityOrder: true,
    });

  public getDependenciesMap = async (
    projectId: number,
  ): Promise<DependenciesMap | undefined> =>
    this.get(`projects/${projectId}/dependency-map`);

  public getPhasesForAProject = async (
    projectId: number,
  ): Promise<ProjectPhase[]> =>
    this.get(`projects/${projectId}/project-phases`);

  public getProjectStatusMapping = async (): Promise<
    Dictionary<ProjectWithStatus>
  > => this.get('projects/status-mapping');

  public getProjectFinancialContractMapping = async (): Promise<
    Dictionary<ProjectWithFinancialContractType>
  > => this.get('projects/financial-contract-mapping');

  public getProjectPartnerMapping = async (
    projectIds: number[],
  ): Promise<Dictionary<ProjectWithPartners>> => {
    const projectIdsString = projectIds.join(',');

    return this.get(`projects/partners?projectIds=${projectIdsString}`);
  };
}

export const ProjectAPI = new ProjectAPIClient();
export * from './types';
export * from './tabs';
